import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData } from '../../services/request'

const STATUS_OPTIONS = ['ALL', 'DRAFT', 'PENDING_PAYMENT', 'PENDING_APPROVAL', 'LIVE', 'CLOSED', 'EXPIRED']

const AdminJobPosts = () => {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [query, setQuery] = useState('')

  const fetchJobs = async () => {
    setLoading(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/jobs`
    const params = {}
    if (statusFilter !== 'ALL') {
      params.status = statusFilter
    }
    const res = await getData(url, {}, params)
    setLoading(false)
    if (res.error === '') {
      if (res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setJobs(Array.isArray(data) ? data : [])
      } else {
        setJobs([])
        setError(res.result?.data?.message || res.result?.data?.data || 'Unable to load jobs')
      }
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Network error')
    }
  }

  useEffect(() => {
    fetchJobs()
  }, [statusFilter])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return jobs
    return jobs.filter((job) => {
      const companyName = job.companyId?.name || job.companyId?.contactPersonName || ''
      return (
        (job.title || '').toLowerCase().includes(q) ||
        companyName.toLowerCase().includes(q) ||
        (job.employmentType || '').toLowerCase().includes(q) ||
        (job.locationType || '').toLowerCase().includes(q) ||
        (job.status || '').toLowerCase().includes(q) ||
        (job.paymentStatus || '').toLowerCase().includes(q)
      )
    })
  }, [jobs, query])

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso || '—'
    }
  }

  const formatCurrency = (amount) => {
    if (amount == null || amount === undefined) return '—'
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount)
  }

  return (
    <section className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Posts</h1>
          <p className="text-sm text-gray-600">
            View all Employ Me job posts and their approval / payment status.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search jobs (title, company, status)"
            className="w-72 max-w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
          />
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-600">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
            >
              {STATUS_OPTIONS.map((st) => (
                <option key={st} value={st}>
                  {st.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            {query.trim() ? 'No job posts match your search' : 'No job posts found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Job Title</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Company</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Payment</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Created</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((job) => (
                  <tr key={job._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/app/employme/job-approvals/${job._id}`}
                        className="font-medium text-gray-900 hover:underline"
                      >
                        {job.title}
                      </Link>
                      <div className="text-xs text-gray-500">
                        {job.employmentType} • {job.locationType}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {job.companyId?.name || job.companyId?.contactPersonName || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-800">
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div>{formatCurrency(job.paymentAmount)}</div>
                      <div className="text-xs text-gray-500">{job.paymentStatus || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(job.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <Link
                          to={`/admin/app/employme/job-approvals/${job._id}`}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          View details
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

export default AdminJobPosts
